// src/components/AuthGuard.jsx
// Schützt Routen vor unauthentifizierten Zugriffen
// Nutzt den globalen AuthContext – macht keinen eigenen Supabase-Call

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { hasRoleAtLeast } from '../utils/roles';
import { protokoll } from '../lib/protokoll';

/**
 * AuthGuard – Protected Route Wrapper
 *
 * @param {React.ReactNode} children     - Die zu schützende Komponente
 * @param {string}          requiredRole - Optional: Minimale Rolle ('player', 'dungeon_assist', 'gm', 'super_admin')
 */
export default function AuthGuard({ children, requiredRole = null }) {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Warten bis Auth-State geladen ist
    if (loading) return;

    // Nicht eingeloggt → Login-Seite
    if (!user) {
      navigate('/', { replace: true });
      return;
    }

    // Rolle prüfen wenn erforderlich
    if (requiredRole && !hasRoleAtLeast(role, requiredRole)) {
      protokoll.warnung('Anmeldung', `Zugriff verweigert: Rolle '${role}' < '${requiredRole}'`);
      navigate('/character', { replace: true });
    }
  }, [loading, user, role, requiredRole, navigate]);

  // Lade-Zustand (globaler Auth-State wird initialisiert)
  if (loading) {
    return <LoadingSpinner message="Authentifizierung..." />;
  }

  // Nicht eingeloggt → null (Redirect passiert oben im useEffect)
  if (!user) return null;

  // Rolle reicht nicht aus → null (Redirect passiert oben im useEffect)
  if (requiredRole && !hasRoleAtLeast(role, requiredRole)) return null;

  // Alles OK → Kinder rendern
  return children;
}
